/**
 * PLAIN WORDING FOR THE SOURCE-DIAGNOSTICS PANEL (polish slice, 2026-10-28).
 *
 * The server sends machine tokens (`status`, `reason_code`) and an ISO timestamp. This module turns
 * ONE row into ONE plain US-English sentence. It changes how a row is worded, never what was asked
 * or answered: the raw tokens stay in the panel's "Show details" fold, untouched.
 *
 * HONEST WORDING. A sentence says what the token says and nothing more. It never names an MLS or
 * provider state the code does not state, and an unknown token is never guessed at: it falls back
 * to "Status: <token>", with the raw code beside it, so the row is never blank.
 *
 * The vocabulary below is the full set the server can send to this panel (read from
 * `src/server/source_cascade.py`, `mls_rung.py`, `cotality.py` and `provider_diag.py`, 2026-10-28):
 *   status      ok, empty, no_match, unavailable, not_available            (RungResult)
 *               live, offline, dormant, disabled                           (Cotality availability)
 *               available, not_available                                   (admin MLS row)
 *   reason_code no_mls_feed, not_configured, no_subject_location, mls_unavailable, no_closed_sales,
 *               not_requested, not_admitted, dormant, off, disabled, unconfigured, forced_offline,
 *               not_connected, provider_down, daily_cap, no_match, subject_locked_to_cad,
 *               not_needed, no_address, cad_unreadable, no_sale_prices, cad_pick, cad_pick_unreadable
 */
import type { ProviderDiagnostic } from "./cascade";

type Row = Pick<ProviderDiagnostic, "rung" | "label" | "status" | "reason_code">;

/** Sentences that depend on WHICH source the row is about. Key: `<rung>:<reason_code>`. */
const BY_RUNG_CODE: Record<string, string> = {
  "mls:no_mls_feed": "MLS was not asked: your account has no MLS feed connected.",
  "mls:not_configured": "MLS was not asked: the MLS feed is not fully set up on this account.",
  "mls:not_admitted": "MLS is not available to this account: it has not been admitted to the MLS feed.",
  "mls:no_subject_location":
    "MLS returned no sales: the subject has no map location to search around.",
  "mls:mls_unavailable": "MLS could not be asked, so no sales were checked.",
  "mls:no_closed_sales": "MLS answered with no closed sales that match this search.",
  "mls:not_requested": "MLS was not asked: comparable sales were not requested in this run.",
  "cotality:dormant": "Cotality was not asked: the connector is parked on this deployment.",
  "cotality:off": "Cotality was not asked: the connector is switched off on this deployment.",
  "cotality:disabled": "Cotality was not asked: the connector is switched off on this deployment.",
  "cotality:unconfigured":
    "Cotality was not asked: the connector is switched on but not configured on this deployment.",
  "cotality:forced_offline":
    "Cotality was not asked: the connector is being held offline by this deployment's settings.",
  "cotality:not_connected": "Cotality was not asked: it is not connected on this account.",
  "cotality:provider_down":
    "Cotality could not be asked: the provider did not answer the last request.",
  "cotality:daily_cap":
    "Cotality was not asked: today's public-records lookup allowance is used up.",
  "cotality:no_match": "Cotality answered: it has no record of this property.",
  "cotality:subject_locked_to_cad":
    "Cotality was not asked: the subject is locked to a county CAD record.",
  "cotality:not_needed":
    "Cotality was not asked: the sources above it already supplied a complete set.",
  "cotality:not_requested": "Cotality was not asked: comparable sales were not requested in this run.",
};

/** Sentences that read the same for any source. `{name}` is the row's label. */
const BY_CODE: Record<string, string> = {
  not_requested: "{name} was not asked: comparable sales were not requested in this run.",
  not_needed: "{name} was not asked: the sources above it already supplied a complete set.",
  no_address: "{name} was not asked: there is no address to look up yet.",
  cad_unreadable: "{name} could not be asked: the county dataset could not be read.",
  no_sale_prices: "{name} answered: its records carry no sale prices to use as comparables.",
  cad_pick: "{name} was read from the county record you picked.",
  cad_pick_unreadable: "{name} could not be asked: the chosen county record could not be read.",
  no_match: "{name} answered: it has no record of this property.",
  daily_cap: "{name} was not asked: today's lookup allowance is used up.",
  provider_down: "{name} could not be asked: the provider did not answer.",
  not_connected: "{name} was not asked: it is not connected on this account.",
};

/** The status alone, when the row carries no (known) reason code. */
const BY_STATUS: Record<string, string> = {
  ok: "{name} answered with results.",
  empty: "{name} answered with nothing to give.",
  no_match: "{name} answered: it has no record of this property.",
  unavailable: "{name} could not be asked.",
  not_available: "{name} is not available here.",
  live: "{name} is answering.",
  offline: "{name} is not answering right now.",
  dormant: "{name} is parked on this deployment.",
  disabled: "{name} is switched off on this deployment.",
  available: "{name} is available.",
};

const fill = (s: string, name: string) => s.replace("{name}", name);

/** The text shown for a source: the server's label, else the rung id, else an honest dash. */
export function diagnosticName(row: Pick<ProviderDiagnostic, "rung" | "label">): string {
  return (row.label || row.rung || "").trim() || "This source";
}

/** One plain sentence for one row. Never empty; never invents a state. */
export function diagnosticSentence(row: Row): string {
  const name = diagnosticName(row);
  const code = (row.reason_code || "").trim();
  const status = (row.status || "").trim();
  const rung = (row.rung || "").trim();
  if (code) {
    const exact = BY_RUNG_CODE[`${rung}:${code}`];
    if (exact) return exact;
    const generic = BY_CODE[code];
    if (generic) return fill(generic, name);
  }
  const byStatus = status ? BY_STATUS[status] : undefined;
  if (byStatus) return fill(byStatus, name);
  // Unknown: say so, show the raw token, invent nothing.
  if (status) return `${name} \u2014 Status: ${status}${code ? ` (reason code: ${code})` : ""}.`;
  if (code) return `${name} \u2014 Status: — (the server sent no status; reason code: ${code}).`;
  return `${name} \u2014 Status: — (the server sent no status or reason).`;
}

/** "Oct 19, 2026, 2:02 PM UTC" from an ISO-8601 UTC timestamp; null when it cannot be read. */
export function plainTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    timeZone: "UTC", timeZoneName: "short",
  });
}
